/**
 * Content-derived definition tables: what the sim can place, what towers do at
 * every upgrade combination, and the doc 04 economy constants. Everything here
 * is resolved from `src/content/` — code never retypes a doc number (doc 12
 * §Code health).
 */

import buildings from '../content/buildings.json' with { type: 'json' };
import towers from '../content/towers.json' with { type: 'json' };
import { DOUBLE_REEL_BONUS } from './const.ts';
import type { Resources, RunModifiers } from './types.ts';

// ---------------------------------------------------------------------------
// tower stats: base + upgrade patches (doc 05)
// ---------------------------------------------------------------------------

export interface TowerStats {
  damage: number;
  rate: number;
  range: number;
  minRange: number;
  ammoPerShot: number;
  powerPerShot: number;
  pierce: number;
  armorIgnore: number;
  critChance: number;
  critMult: number;
  bleedDps: number;
  bleedDur: number;
  bleedSlow: number;
  bleedSpreadRadius: number;
  chains: number;
  chainFalloff: number;
  stun: number;
  vsSwiftMult: number;
  burnDps: number;
  burnDur: number;
  splashRadius: number;
  bombletCount: number;
  bombletDmg: number;
  bombletRadius: number;
  bombletStun: number;
  knockback: number;
  slowPct: number;
  slowDur: number;
  // — Laser Pointer (doc 05 §4): a beam that ramps while it holds one target —
  beamDps: number;
  rampMult: number;
  rampSeconds: number;
  /** Seconds the ramp survives a target switch ("Unblinking"). */
  rampHold: number;
  beams: number;
  beamSplit: number;
  /** Cloaked reveal radius; everything lit inside it takes `litAmp` more. */
  revealRadius: number;
  litAmp: number;
  // — Fan Enjoyer (doc 05 §5): a field, not a gun —
  fieldRadius: number;
  fieldSlow: number;
  /** Extra slow applied to Swift species inside the field. */
  swiftStrip: number;
  pushEvery: number;
  pushTiles: number;
  pushRadius: number;
  pushDamage: number;
  pullRate: number;
  pullRadius: number;
  /** Enemies caught in the vortex take this much more from splash. */
  clusterAmp: number;
  // — Subscription Canceller (doc 05 §6) —
  torpedoes: number;
  vsBossMult: number;
  splashDamage: number;
  /** 1 = the splash lands full damage instead of `splashDamage` ("Reply-All"). */
  splashFull: number;
  armorShredPct: number;
  armorShredDur: number;
  /** Watts drawn while working; the grid pays for beams and fans (doc 04). */
  powerPerSecond: number;
}

/** Doc 05: T1 = 20 · T2 = 35 · T3 = 60 alloy, uniform across towers. */
export const TIER_COSTS = [towers.tierCosts.t1, towers.tierCosts.t2, towers.tierCosts.t3];

export const TOWER_BASE: Record<string, Partial<TowerStats>> = {
  stabby: {
    damage: towers.towers.stabby.damage,
    rate: towers.towers.stabby.rate,
    range: towers.towers.stabby.range,
    ammoPerShot: towers.towers.stabby.ammoPerShot,
  },
  zappy_boi: {
    damage: towers.towers.zappy_boi.damage,
    rate: towers.towers.zappy_boi.rate,
    range: towers.towers.zappy_boi.range,
    chains: towers.towers.zappy_boi.chains,
    chainFalloff: towers.towers.zappy_boi.chainFalloff,
    powerPerShot: towers.towers.zappy_boi.powerPerShot,
  },
  yeet_cannon: {
    damage: towers.towers.yeet_cannon.damage,
    rate: towers.towers.yeet_cannon.rate,
    range: towers.towers.yeet_cannon.range,
    minRange: towers.towers.yeet_cannon.minRange,
    ammoPerShot: towers.towers.yeet_cannon.ammoPerShot,
    splashRadius: towers.towers.yeet_cannon.splashRadius,
  },
  laser_pointer: {
    beamDps: towers.towers.laser_pointer.beamDps,
    rampMult: towers.towers.laser_pointer.rampMult,
    rampSeconds: towers.towers.laser_pointer.rampSeconds,
    beams: towers.towers.laser_pointer.beams,
    beamSplit: towers.towers.laser_pointer.beamSplit,
    range: towers.towers.laser_pointer.range,
    powerPerSecond: towers.towers.laser_pointer.powerPerSecond,
    rate: 30, // the beam is continuous: it "fires" every tick it holds a target
  },
  fan_enjoyer: {
    fieldRadius: towers.towers.fan_enjoyer.fieldRadius,
    fieldSlow: towers.towers.fan_enjoyer.fieldSlow,
    range: towers.towers.fan_enjoyer.range,
    powerPerSecond: towers.towers.fan_enjoyer.powerPerSecond,
    rate: 30,
  },
  subscription_canceller: {
    damage: towers.towers.subscription_canceller.damage,
    rate: towers.towers.subscription_canceller.rate,
    range: towers.towers.subscription_canceller.range,
    ammoPerShot: towers.towers.subscription_canceller.ammoPerShot,
    torpedoes: towers.towers.subscription_canceller.torpedoes,
    vsBossMult: towers.towers.subscription_canceller.vsBossMult,
  },
};

const statsCache = new Map<string, TowerStats>();

export function towerStats(def: string, up: { a: number; b: number; c: number }): TowerStats {
  const key = `${def}:${up.a}${up.b}${up.c}`;
  let s = statsCache.get(key);
  if (s) return s;
  s = {
    damage: 0, rate: 1, range: 0, minRange: 0, ammoPerShot: 0, powerPerShot: 0,
    pierce: 1, armorIgnore: 0, critChance: 0, critMult: 1,
    bleedDps: 0, bleedDur: 0, bleedSlow: 0, bleedSpreadRadius: 0,
    chains: 1, chainFalloff: 0, stun: 0, vsSwiftMult: 1, burnDps: 0, burnDur: 0,
    splashRadius: 0, bombletCount: 0, bombletDmg: 0, bombletRadius: 1, bombletStun: 0,
    knockback: 0, slowPct: 0, slowDur: 0,
    beamDps: 0, rampMult: 1, rampSeconds: 1, rampHold: 0, beams: 1, beamSplit: 1,
    revealRadius: 0, litAmp: 0,
    fieldRadius: 0, fieldSlow: 0, swiftStrip: 0,
    pushEvery: 0, pushTiles: 0, pushRadius: 0, pushDamage: 0,
    pullRate: 0, pullRadius: 0, clusterAmp: 0,
    torpedoes: 1, vsBossMult: 1, splashDamage: 0, splashFull: 0,
    armorShredPct: 0, armorShredDur: 0, powerPerSecond: 0,
    ...TOWER_BASE[def],
  };
  const paths = (towers.towers as Record<string, { paths?: Record<string, { tiers?: Array<{ patch: Record<string, number> }> }> }>)[def]?.paths;
  if (paths) {
    for (const p of ['a', 'b', 'c'] as const) {
      const tiers = paths[p]?.tiers;
      if (!tiers) continue;
      for (let t = 0; t < up[p]; t++) {
        Object.assign(s, tiers[t]!.patch);
      }
    }
  }
  statsCache.set(key, s);
  return s;
}

/**
 * Doc 05's soft gate, as a rule the sim can enforce: paths A and B tiers 1–2
 * are always open; A-T3 at level 2, B-T3 at level 3, path C at 4, its capstone
 * at 5. The XP thresholds behind those levels are content (doc 08).
 */
export function tierCaps(level: number): { a: number; b: number; c: number } {
  return {
    a: level >= 2 ? 3 : 2,
    b: level >= 3 ? 3 : 2,
    c: level >= 5 ? 3 : level >= 4 ? 2 : 0,
  };
}

export interface PlaceableDef {
  name: string;
  label: string;
  kind: 'wall' | 'tower' | 'building';
  size: number;
  hp: number;
  cost: Partial<Record<keyof Resources, number>>;
  /** Net power at full efficiency; negative = consumer (doc 04). */
  power: number;
  produces?: Partial<Record<keyof Resources, number>>;
  consumes?: Partial<Record<keyof Resources, number>>;
  /** Extractors must cover their deposit (doc 03/04). */
  wants?: 'vein' | 'vent';
  relayRadius?: number;
  storagePerResource?: number;
  /** Repair Bay: hp restored per round at settlement (doc 04). */
  healRatePerRound?: number;
}

export const B = buildings.buildings;

/**
 * What the sim can place and run: the doc 05 starter roster (stage 3), the
 * wall, and both economy chains. Meta-unlock towers and the Repair Bay arrive
 * with the meta (stages 4/5).
 */
export const PLACEABLE: Record<string, PlaceableDef> = {
  wall: { name: B.wall.name, label: B.wall.label, kind: 'wall', size: B.wall.size, hp: B.wall.hp, cost: B.wall.cost, power: 0 },
  stabby: {
    name: towers.towers.stabby.name,
    label: towers.towers.stabby.label,
    kind: 'tower',
    size: towers.towers.stabby.size,
    hp: towers.towers.stabby.hp,
    cost: towers.towers.stabby.cost,
    power: 0,
  },
  zappy_boi: {
    name: towers.towers.zappy_boi.name,
    label: towers.towers.zappy_boi.label,
    kind: 'tower',
    size: towers.towers.zappy_boi.size,
    hp: towers.towers.zappy_boi.hp,
    cost: towers.towers.zappy_boi.cost,
    power: 0,
  },
  yeet_cannon: {
    name: towers.towers.yeet_cannon.name,
    label: towers.towers.yeet_cannon.label,
    kind: 'tower',
    size: towers.towers.yeet_cannon.size,
    hp: towers.towers.yeet_cannon.hp,
    cost: towers.towers.yeet_cannon.cost,
    power: 0,
  },
  // Meta unlocks (doc 08). They draw power instead of eating ammo, except the
  // Canceller, which eats a LOT of ammo — doc 05's consumption identity.
  laser_pointer: {
    name: towers.towers.laser_pointer.name,
    label: towers.towers.laser_pointer.label,
    kind: 'tower',
    size: towers.towers.laser_pointer.size,
    hp: towers.towers.laser_pointer.hp,
    cost: towers.towers.laser_pointer.cost,
    power: -towers.towers.laser_pointer.powerPerSecond,
  },
  fan_enjoyer: {
    name: towers.towers.fan_enjoyer.name,
    label: towers.towers.fan_enjoyer.label,
    kind: 'tower',
    size: towers.towers.fan_enjoyer.size,
    hp: towers.towers.fan_enjoyer.hp,
    cost: towers.towers.fan_enjoyer.cost,
    power: -towers.towers.fan_enjoyer.powerPerSecond,
  },
  subscription_canceller: {
    name: towers.towers.subscription_canceller.name,
    label: towers.towers.subscription_canceller.label,
    kind: 'tower',
    size: towers.towers.subscription_canceller.size,
    hp: towers.towers.subscription_canceller.hp,
    cost: towers.towers.subscription_canceller.cost,
    power: 0,
  },
  ore_rig: { name: B.ore_rig.name, label: B.ore_rig.label, kind: 'building', size: B.ore_rig.size, hp: B.ore_rig.hp, cost: B.ore_rig.cost, power: B.ore_rig.power, produces: B.ore_rig.produces, wants: 'vein' },
  smelter: { name: B.smelter.name, label: B.smelter.label, kind: 'building', size: B.smelter.size, hp: B.smelter.hp, cost: B.smelter.cost, power: B.smelter.power, consumes: B.smelter.consumes, produces: B.smelter.produces },
  bullet_kitchen: { name: B.bullet_kitchen.name, label: B.bullet_kitchen.label, kind: 'building', size: B.bullet_kitchen.size, hp: B.bullet_kitchen.hp, cost: B.bullet_kitchen.cost, power: B.bullet_kitchen.power, consumes: B.bullet_kitchen.consumes, produces: B.bullet_kitchen.produces },
  gas_tap: { name: B.gas_tap.name, label: B.gas_tap.label, kind: 'building', size: B.gas_tap.size, hp: B.gas_tap.hp, cost: B.gas_tap.cost, power: B.gas_tap.power, produces: B.gas_tap.produces, wants: 'vent' },
  refinery: { name: B.refinery.name, label: B.refinery.label, kind: 'building', size: B.refinery.size, hp: B.refinery.hp, cost: B.refinery.cost, power: B.refinery.power, consumes: B.refinery.consumes, produces: B.refinery.produces },
  generator: { name: B.generator.name, label: B.generator.label, kind: 'building', size: B.generator.size, hp: B.generator.hp, cost: B.generator.cost, power: B.generator.power, consumes: B.generator.consumes },
  power_pylon: { name: B.power_pylon.name, label: B.power_pylon.label, kind: 'building', size: B.power_pylon.size, hp: B.power_pylon.hp, cost: B.power_pylon.cost, power: B.power_pylon.power, relayRadius: B.power_pylon.relayRadius },
  storage_depot: { name: B.storage_depot.name, label: B.storage_depot.label, kind: 'building', size: B.storage_depot.size, hp: B.storage_depot.hp, cost: B.storage_depot.cost, power: B.storage_depot.power, storagePerResource: B.storage_depot.storagePerResource },
  repair_bay: { name: B.repair_bay.name, label: B.repair_bay.label, kind: 'building', size: B.repair_bay.size, hp: B.repair_bay.hp, cost: B.repair_bay.cost, power: B.repair_bay.power, healRatePerRound: B.repair_bay.healRatePerRound },
};

export function placeableDef(def: string): PlaceableDef | undefined {
  return PLACEABLE[def];
}

// Doc 04 numbers, all straight from the content file (statDrift-proof):
export const STORAGE_BASE = buildings.storage.baseCapPerResource;
export const STORAGE_PER_DEPOT = buildings.storage.perDepot;
export const CAPACITOR_MAX = buildings.capacitor.max;
export const CAPACITOR_RATE = buildings.capacitor.chargeRateMax;
export const GRID_RADIUS = buildings.grid.emitRadius;
export const CORE_POWER = B.core_dome.power;
export const GENERATOR_POWER = B.generator.power;
export const GENERATOR_BURN = B.generator.consumes.fuel;
export const DEPOSIT_STOCK = { vein: buildings.deposits.oreVein, vent: buildings.deposits.gasVent };

export const RES_IDS = ['ore', 'alloy', 'ammo', 'gas', 'fuel'] as const;

/**
 * A run with no meta behind it: every multiplier 1, nothing gated. The harness,
 * the golden replays and any test build a sim with these, so stage 4's meta can
 * never move a blessed hash by accident (doc 08 / doc 12 §T4).
 */
export const NEUTRAL_MODIFIERS: RunModifiers = {
  coreHpMult: 1,
  structureHpMult: 1,
  storageMult: 1,
  doubleReelBonus: DOUBLE_REEL_BONUS,
  towerLevels: null,
  allowedDefs: null,
  allowedAbilities: null,
  enemyHpMult: 1,
  bossHpMult: 1,
  gimmickRateMult: 1,
  costMult: 1,
  sellRefundMult: 1,
  autoLureSeconds: 0,
};

/** What a structure is doing right now — derived each tick for the UI, never hashed. */
export type EconStatus = 'working' | 'stalled' | 'offgrid';
